/**
 * LocationPrompt
 * ==============
 * Banner não-intrusivo que pergunta a preferência de localização do usuário.
 * Aparece apenas em rotas públicas e somente quando ainda não há preferência
 * salva. Três caminhos:
 *   1. Usar GPS  → dispara `requestLocation()` (browser nativo)
 *   2. Escolher cidade  → abre seletor com lista de municípios
 *   3. Agora não  → marca como `denied`, não pergunta mais
 *
 * O usuário pode mudar a escolha depois clicando no ícone de localização
 * no Header.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { MapPin, X, Navigation, Search, Loader2 } from 'lucide-react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { useLocation } from '../context/LocationContext';
import { municipiosApi } from '../services/api';

export default function LocationPrompt() {
  const { showPrompt, closePrompt, requestLocation, setManualLocation, denyLocation, loading } = useLocation();
  const { pathname } = useRouterLocation();
  const [step, setStep] = useState('initial'); // 'initial' | 'pick'
  const [municipios, setMunicipios] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingMun, setLoadingMun] = useState(false);

  useEffect(() => {
    if (!showPrompt) {
      // reset quando fechar
      setStep('initial');
      setSearch('');
    }
  }, [showPrompt]);

  useEffect(() => {
    if (step !== 'pick' || municipios.length > 0) return;
    setLoadingMun(true);
    municipiosApi
      .list()
      .then((list) => setMunicipios(list || []))
      .catch(() => {})
      .finally(() => setLoadingMun(false));
  }, [step, municipios.length]);

  const filtered = useMemo(() => {
    if (!search.trim()) return municipios.slice(0, 20);
    const q = search.toLowerCase().trim();
    return municipios
      .filter((m) => (m.nome || '').toLowerCase().includes(q) || (m.uf || '').toLowerCase().includes(q))
      .slice(0, 20);
  }, [municipios, search]);

  // Não mostrar em rotas de checkout do Delivery (cardápio público já tem
  // contexto explícito via slug do município) nem no painel do empreendedor.
  if (pathname && (pathname.startsWith('/delivery/') || pathname.startsWith('/empreendedor/delivery/'))) return null;

  if (!showPrompt) return null;

  const handleGPS = () => {
    requestLocation();
    closePrompt();
  };

  const handlePick = (m) => {
    setManualLocation({
      city: m.nome,
      uf: m.uf,
      latitude: m.latitude || null,
      longitude: m.longitude || null,
    });
  };

  return (
    <div
      role="dialog"
      aria-label="Definir sua localização"
      data-testid="location-prompt"
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        maxWidth: 440,
        margin: '0 auto',
        background: 'white',
        borderRadius: 16,
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        // Mais alto que o CookieConsent (z-[9999]) — assim o prompt fica
        // sempre clicável mesmo com o banner de cookies aberto.
        zIndex: 10000,
        border: '1px solid #E9D5FF',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #56007F, #7B1FA2)',
          color: 'white',
        }}
      >
        <MapPin size={18} />
        <span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>
          {step === 'initial' ? 'Onde você está?' : 'Escolha sua cidade'}
        </span>
        <button
          type="button"
          onClick={closePrompt}
          aria-label="Fechar"
          data-testid="location-prompt-close"
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: 26,
            height: 26,
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={14} />
        </button>
      </div>

      {step === 'initial' ? (
        <div style={{ padding: 16 }}>
          <p style={{ fontSize: 13, color: '#444', margin: 0 }}>
            Para mostrar negócios e ofertas perto de você, podemos usar sua
            localização ou você pode escolher uma cidade.
          </p>
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              onClick={handleGPS}
              disabled={loading}
              data-testid="location-prompt-gps"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                background: '#56007F',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                padding: '11px 14px',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Navigation size={16} />}
              Usar minha localização
            </button>
            <button
              type="button"
              onClick={() => setStep('pick')}
              data-testid="location-prompt-manual"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                background: 'white',
                color: '#56007F',
                border: '1px solid #56007F',
                borderRadius: 10,
                padding: '11px 14px',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <Search size={16} />
              Escolher cidade
            </button>
            <button
              type="button"
              onClick={denyLocation}
              data-testid="location-prompt-deny"
              style={{
                background: 'transparent',
                color: '#888',
                border: 'none',
                fontSize: 12,
                padding: 6,
                cursor: 'pointer',
              }}
            >
              Agora não
            </button>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              style={{ position: 'absolute', left: 10, top: 11, color: '#888' }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar cidade ou estado..."
              data-testid="location-prompt-search"
              style={{
                width: '100%',
                paddingLeft: 30,
                padding: '9px 12px 9px 30px',
                border: '1px solid #ddd',
                borderRadius: 8,
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div
            style={{
              marginTop: 10,
              maxHeight: 220,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 8,
            }}
            data-testid="location-prompt-list"
          >
            {loadingMun ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
                <Loader2 size={18} className="animate-spin inline-block mr-2" />
                Carregando...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 18, textAlign: 'center', color: '#888', fontSize: 13 }}>
                Nenhuma cidade encontrada
              </div>
            ) : (
              filtered.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handlePick(m)}
                  data-testid={`location-prompt-pick-${m.id}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'white',
                    border: 'none',
                    borderBottom: '1px solid #f1f1f1',
                    padding: '9px 12px',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#FAF3FF')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                >
                  <strong>{m.nome}</strong> <span style={{ color: '#888' }}>· {m.uf}</span>
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            onClick={() => setStep('initial')}
            data-testid="location-prompt-back"
            style={{
              marginTop: 10,
              background: 'transparent',
              color: '#56007F',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              padding: 4,
              cursor: 'pointer',
            }}
          >
            ← Voltar
          </button>
        </div>
      )}
    </div>
  );
}
